/**
 * ListingHeader — encabezado unificado para listados del config.
 *
 * Franja superior con: título + contador (X de Y), buscador opcional y un slot `actions`
 * para filtros/selectores propios de cada listado. Mismo lenguaje visual que el header de
 * Publicaciones. Reutilizable — NO reescribir headers ad-hoc por sección.
 *
 * Ver: docs/design-system/COMPONENT_CATALOG.md
 */
import React from 'react';
import { Search } from 'lucide-react';

interface SearchConfig {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

interface Props {
  title?: string;
  /** Cantidad tras filtros. */
  count?: number;
  /** Cantidad total sin filtrar. */
  total?: number;
  /** Sustantivo para el contador: "usuario" -> "3 de 10 usuarios". */
  itemNoun?: string;
  search?: SearchConfig;
  /** Filtros/controles extra a la derecha (selects, toggles, botones). */
  actions?: React.ReactNode;
  className?: string;
}

const ListingHeader: React.FC<Props> = ({
  title,
  count,
  total,
  itemNoun = 'item',
  search,
  actions,
  className = '',
}) => {
  const showCounter = typeof count === 'number' && typeof total === 'number';
  const plural = (total ?? 0) !== 1 ? 's' : '';

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-t-lg ${className}`}
    >
      {/* Izquierda: título + contador */}
      <div className="min-w-0">
        {title && <h4 className="text-sm font-semibold text-gray-900 truncate">{title}</h4>}
        {showCounter && (
          <p className="text-xs text-gray-500 tabular-nums">
            {count!.toLocaleString('es-AR')} de {total!.toLocaleString('es-AR')} {itemNoun}
            {plural}
          </p>
        )}
      </div>

      {/* Derecha: buscador + acciones */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {search && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || 'Buscar...'}
              className="w-full sm:w-64 pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}
        {actions}
      </div>
    </div>
  );
};

export default ListingHeader;
